import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { workspaceAdminAPI } from '../services/api';
import { KpiDetailPanel } from '../components/common/ClickableKpiCard';
import { Users, Building, Activity, UserCheck, UserX, TrendingUp, Clock3, RefreshCw, ArrowRight, AlertTriangle, ShieldCheck, Sparkles, PlusCircle, Layers3, CreditCard } from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { useTableControls } from '../hooks/useTableControls';
import { TableControls } from '../components/ui/table-controls';
import { formatCurrencyAmount } from '../lib/currency';
import { chartColor, tooltipContentStyle, tooltipCursor, axisProps, gridProps } from '../lib/chartTheme';
import { EmptyState, StatSkeletonGrid } from '../components/common/States';
import { StatCard } from '../components/common/StatCard';

const SuperadminOverviewPage = ({ onNavigate }) => {
  const [dashboardStats, setDashboardStats] = useState({
    total_tenants: 0,
    total_users: 0,
    active_users: 0,
    system_health: 100,
  });
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [billingDashboard, setBillingDashboard] = useState({ summary: {} });
  const [billingSubscriptions, setBillingSubscriptions] = useState([]);
  const [dateRange, setDateRange] = useState('7d');
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [overviewError, setOverviewError] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [overviewCardDetail, setOverviewCardDetail] = useState(null);

  const fetchOverviewData = async () => {
    try {
      setLoading(true);
      setOverviewError('');
      const [statsResponse, tenantsResponse, usersResponse, auditResponse, plansResponse, billingDashboardResponse, billingSubscriptionsResponse] = await Promise.all([
        workspaceAdminAPI.getDashboardStats(),
        workspaceAdminAPI.listTenants(),
        workspaceAdminAPI.listUsers(),
        workspaceAdminAPI.getAuditLogs(),
        workspaceAdminAPI.listSubscriptionPlans({ include_inactive: true }),
        workspaceAdminAPI.getBillingDashboard(),
        workspaceAdminAPI.listBillingSubscriptions(),
      ]);
      setDashboardStats(statsResponse.data || {});
      setTenants(Array.isArray(tenantsResponse.data) ? tenantsResponse.data : []);
      setUsers(Array.isArray(usersResponse.data) ? usersResponse.data : []);
      setAuditLogs(Array.isArray(auditResponse.data) ? auditResponse.data : []);
      setSubscriptionPlans(Array.isArray(plansResponse.data) ? plansResponse.data : []);
      setBillingDashboard(billingDashboardResponse.data || { summary: {} });
      setBillingSubscriptions(Array.isArray(billingSubscriptionsResponse.data) ? billingSubscriptionsResponse.data : []);
      setLastUpdatedAt(new Date());
    } catch (error) {
      console.error('Failed to fetch superadmin overview data:', error);
      setOverviewError('Failed to load overview analytics. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverviewData();
  }, []);

  const tenantNameMap = useMemo(() => new Map(tenants.map((tenant) => [tenant.id, tenant.name])), [tenants]);

  const activeUsers = useMemo(() => {
    if (users.length > 0) return users.filter((entry) => entry.is_active).length;
    return dashboardStats.active_users || 0;
  }, [users, dashboardStats.active_users]);

  const inactiveUsers = useMemo(() => {
    const totalUsers = users.length > 0 ? users.length : dashboardStats.total_users || 0;
    return Math.max(totalUsers - activeUsers, 0);
  }, [users.length, dashboardStats.total_users, activeUsers]);

  const usersPerTenant = useMemo(() => {
    const counts = new Map();
    users.forEach((entry) => {
      const key = entry.tenant_id || 0;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    const rows = [...counts.entries()].map(([tenantId, count]) => ({
      name: tenantNameMap.get(tenantId) || `Tenant #${tenantId}`,
      users: count,
    }));
    return rows.sort((a, b) => b.users - a.users).slice(0, 6);
  }, [users, tenantNameMap]);

  const tenantStatusData = useMemo(() => {
    const statusCounts = tenants.reduce((acc, tenant) => {
      const key = (tenant.status || 'unknown').toLowerCase();
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    return [
      { name: 'Active', value: statusCounts.active || 0, color: '#10B981' },
      { name: 'Suspended', value: statusCounts.suspended || 0, color: '#EF4444' },
      { name: 'Pending', value: statusCounts.pending || 0, color: '#F59E0B' },
    ].filter((entry) => entry.value > 0);
  }, [tenants]);

  const userStatusData = useMemo(
    () => [
      { name: 'Active', value: activeUsers, color: '#10B981' },
      { name: 'Inactive', value: inactiveUsers, color: '#EF4444' },
    ],
    [activeUsers, inactiveUsers]
  );

  const isWithinSelectedRange = useCallback((createdAt) => {
    if (!createdAt) return false;
    const eventDate = new Date(createdAt);
    if (Number.isNaN(eventDate.getTime())) return false;
    const now = new Date();
    if (dateRange === 'today') return eventDate.toDateString() === now.toDateString();
    const days = dateRange === '30d' ? 30 : 7;
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - (days - 1));
    return eventDate >= start;
  }, [dateRange]);

  const filteredAuditLogs = useMemo(() => {
    const sorted = [...auditLogs].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return sorted.filter((entry) => isWithinSelectedRange(entry.created_at));
  }, [auditLogs, isWithinSelectedRange]);

  const actionTrendData = useMemo(() => {
    const actionCounts = filteredAuditLogs.reduce((acc, entry) => {
      const action = (entry.action || 'other').toLowerCase();
      const key = action.includes('create')
        ? 'create'
        : action.includes('update')
          ? 'update'
          : action.includes('delete')
            ? 'delete'
            : action.includes('login')
              ? 'login'
              : 'other';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    return [
      { action: 'Create', count: actionCounts.create || 0 },
      { action: 'Update', count: actionCounts.update || 0 },
      { action: 'Delete', count: actionCounts.delete || 0 },
      { action: 'Login', count: actionCounts.login || 0 },
      { action: 'Other', count: actionCounts.other || 0 },
    ];
  }, [filteredAuditLogs]);

  const recentAuditTable = useTableControls(filteredAuditLogs, {
    initialSortKey: 'created_at',
    initialSortDirection: 'desc',
  });
  const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : 'N/A');
  const formatLastUpdated = (value) =>
    value
      ? new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      : 'N/A';

  const totalUsers = users.length > 0 ? users.length : dashboardStats.total_users || 0;
  const totalTenants = tenants.length > 0 ? tenants.length : dashboardStats.total_tenants || 0;
  const averageUsersPerTenant = totalTenants > 0 ? (totalUsers / totalTenants).toFixed(1) : '0.0';
  const failedAuditCount = filteredAuditLogs.filter((entry) => String(entry.outcome || '').toLowerCase() !== 'success').length;
  const suspendedTenantsCount = tenants.filter((tenant) => String(tenant.status || '').toLowerCase() === 'suspended').length;

  const platformSignals = [
    {
      label: 'System Health',
      value: `${dashboardStats.system_health ?? 100}%`,
      description: 'Availability and platform service confidence',
      tone: 'text-emerald-600',
      tab: 'system',
    },
    {
      label: 'Suspended Tenants',
      value: suspendedTenantsCount,
      description: 'Requires commercial or compliance follow-up',
      tone: suspendedTenantsCount > 0 ? 'text-amber-600' : 'text-emerald-600',
      tab: 'tenants',
    },
    {
      label: 'Failed Audit Events',
      value: failedAuditCount,
      description: `Detected in the selected ${dateRange} range`,
      tone: failedAuditCount > 0 ? 'text-rose-600' : 'text-emerald-600',
      tab: 'audit',
    },
  ];

  const recentTenants = useMemo(() => {
    return [...tenants]
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      .slice(0, 5)
      .map((tenant) => ({
        id: tenant.id,
        name: tenant.name || tenant.slug || `Tenant #${tenant.id}`,
        status: tenant.status || 'unknown',
        createdAt: tenant.created_at,
        plan: tenant.subscription_plan_name || tenant.subscription_plan_code || 'Plan pending',
      }));
  }, [tenants]);

  const adminEmailByTenantId = useMemo(() => {
    const map = new Map();
    users.forEach((user) => {
      if (String(user.role || '').toLowerCase() === 'workspace_admin' && !map.has(user.tenant_id)) {
        map.set(user.tenant_id, user.email);
      }
    });
    return map;
  }, [users]);

  const activePlansCount = useMemo(
    () => subscriptionPlans.filter((plan) => Boolean(plan?.is_active)).length,
    [subscriptionPlans]
  );

  const pendingRequestsCount = useMemo(
    () => tenants.filter((tenant) => String(tenant.status || '').toLowerCase() === 'pending').length,
    [tenants]
  );

  const onboardingGrowth = useMemo(() => {
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    const currentWindowStart = now - 30 * dayMs;
    const previousWindowStart = now - 60 * dayMs;

    const currentCount = tenants.filter((tenant) => {
      const createdAt = new Date(tenant.created_at || 0).getTime();
      return createdAt >= currentWindowStart;
    }).length;

    const previousCount = tenants.filter((tenant) => {
      const createdAt = new Date(tenant.created_at || 0).getTime();
      return createdAt >= previousWindowStart && createdAt < currentWindowStart;
    }).length;

    if (previousCount === 0) {
      return currentCount > 0 ? 100 : 0;
    }

    return ((currentCount - previousCount) / previousCount) * 100;
  }, [tenants]);

  const referenceCards = useMemo(
    () => [
      {
        title: 'Active Plans',
        value: loading ? '...' : activePlansCount,
        hint: 'Published subscription plans',
        icon: Layers3,
        accent: 'text-violet-600',
        onClick: () => onNavigate('plans/subscription-plans'),
      },
      {
        title: 'Pending Requests',
        value: loading ? '...' : pendingRequestsCount,
        hint: 'Organizations waiting for action',
        icon: AlertTriangle,
        accent: 'text-amber-500',
        onClick: () => onNavigate('tenants'),
      },
      {
        title: 'Monthly Growth',
        value: loading ? '...' : `${onboardingGrowth >= 0 ? '+' : ''}${onboardingGrowth.toFixed(0)}%`,
        hint: 'Organization onboarding vs previous 30 days',
        icon: TrendingUp,
        accent: onboardingGrowth >= 0 ? 'text-emerald-500' : 'text-rose-600',
        onClick: () => onNavigate('tenants'),
      },
      {
        title: 'Total Revenue',
        value: loading ? '...' : formatCurrencyAmount(billingDashboard.summary?.payments_collected || 0, 'INR'),
        hint: 'Collected revenue across billing accounts',
        icon: CreditCard,
        accent: 'text-amber-500',
        onClick: () => onNavigate('billing/collected'),
      },
    ],
    [activePlansCount, billingDashboard.summary, loading, onboardingGrowth, onNavigate, pendingRequestsCount]
  );

  const recentRegisteredCompanies = useMemo(
    () =>
      recentTenants.map((tenant) => ({
        ...tenant,
        adminEmail: adminEmailByTenantId.get(tenant.id) || 'Admin not assigned',
      })),
    [adminEmailByTenantId, recentTenants]
  );

  const topPerformingPlans = useMemo(() => {
    const buckets = new Map();

    billingSubscriptions.forEach((row) => {
      const key = row.plan_name || 'Unassigned';
      const current = buckets.get(key) || { planName: key, subscribers: 0, revenue: 0 };
      current.subscribers += 1;
      current.revenue += Number(row.monthly_revenue || 0);
      buckets.set(key, current);
    });

    return [...buckets.values()]
      .sort((a, b) => b.revenue - a.revenue || b.subscribers - a.subscribers)
      .slice(0, 5);
  }, [billingSubscriptions]);

  const formatRelativeAge = (value) => {
    if (!value) return 'Recently';
    const createdAt = new Date(value);
    if (Number.isNaN(createdAt.getTime())) return 'Recently';

    const diffMs = Date.now() - createdAt.getTime();
    const diffDays = Math.max(Math.floor(diffMs / (24 * 60 * 60 * 1000)), 0);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 30) return `${diffDays} days ago`;
    const diffMonths = Math.floor(diffDays / 30);
    return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
  };

  const tenantWatchlist = useMemo(() => {
    const usersByTenant = new Map();
    users.forEach((entry) => {
      const key = entry.tenant_id || 0;
      usersByTenant.set(key, (usersByTenant.get(key) || 0) + 1);
    });

    return tenants
      .map((tenant) => {
        const status = String(tenant.status || 'unknown').toLowerCase();
        const tenantUsers = usersByTenant.get(tenant.id) || 0;
        let priority = 0;
        if (status === 'suspended') priority += 3;
        if (status === 'pending') priority += 2;
        if (tenantUsers === 0) priority += 1;
        return {
          id: tenant.id,
          name: tenant.name || tenant.slug || `Tenant #${tenant.id}`,
          status,
          users: tenantUsers,
          priority,
        };
      })
      .sort((a, b) => b.priority - a.priority || a.users - b.users)
      .slice(0, 5);
  }, [tenants, users]);

  const kpiCards = [
    { title: 'Total Tenants', value: totalTenants, hint: 'Organizations onboarded', icon: Building, tone: 'primary', onClick: () => onNavigate('tenants') },
    { title: 'Total Users', value: totalUsers.toLocaleString(), hint: 'Across all organizations', icon: Users, tone: 'info', onClick: () => onNavigate('users') },
    { title: 'Active Users', value: activeUsers.toLocaleString(), hint: `${totalUsers > 0 ? ((activeUsers / totalUsers) * 100).toFixed(1) : 0}% active ratio`, icon: UserCheck, tone: 'success', onClick: () => onNavigate('users') },
    { title: 'Inactive Users', value: inactiveUsers.toLocaleString(), hint: 'Needs review / reactivation', icon: UserX, tone: 'danger', onClick: () => onNavigate('users') },
    { title: 'System Health', value: `${dashboardStats.system_health ?? 100}%`, hint: 'Platform availability', icon: Activity, tone: 'success', onClick: () => onNavigate('system') },
    { title: 'Users / Tenant', value: averageUsersPerTenant, hint: 'Average tenant density', icon: TrendingUp, tone: 'warning', onClick: () => onNavigate('tenants') },
  ];

  return (
    <div className="space-y-6">
      {overviewError ? (
        <Card className="border-danger/40 bg-danger/10">
          <CardContent className="pt-6 text-danger">{overviewError}</CardContent>
        </Card>
      ) : null}

      <Card className="group relative overflow-hidden border-primary/20 bg-gradient-to-br from-primary/10 via-card to-info/10 text-foreground shadow-sm">
        <CardContent className="pt-6">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -left-16 top-0 h-40 w-40 rounded-full bg-info/10 blur-3xl transition duration-700 group-hover:scale-110" />
            <div className="absolute right-0 top-8 h-44 w-44 rounded-full bg-primary/10 blur-3xl transition duration-700 group-hover:scale-110" />
          </div>
          <div className="grid gap-6 xl:grid-cols-[1.25fr_0.75fr]">
            <div className="space-y-5">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/80 px-3 py-1 text-xs font-medium text-primary shadow-sm backdrop-blur-sm">
                <Sparkles className="h-3.5 w-3.5 text-primary transition-transform duration-300 group-hover:rotate-12" />
                Platform command center
              </div>
              <div className="space-y-2">
                <h2 className="text-3xl font-semibold tracking-tight text-foreground">Operate every tenant from one executive control layer</h2>
                <p className="max-w-3xl text-sm text-muted-foreground">
                  Review tenant growth, audit risk, platform health, and user activation before switching into deeper control-plane workflows.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-2xl border border-border bg-card/90 p-4 shadow-sm backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:shadow-md">
                  <p className="text-xs font-medium text-muted-foreground">Tenants</p>
                  <p className="mt-2 text-2xl font-semibold text-foreground">{totalTenants}</p>
                  <p className="mt-1 text-xs text-muted-foreground">Multi-tenant portfolio under management</p>
                </div>
                <div className="rounded-2xl border border-border bg-card/90 p-4 shadow-sm backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:shadow-md">
                  <p className="text-xs font-medium text-muted-foreground">Users</p>
                  <p className="mt-2 text-2xl font-semibold text-foreground">{totalUsers.toLocaleString()}</p>
                  <p className="mt-1 text-xs text-muted-foreground">Identity footprint across workspaces</p>
                </div>
                <div className="rounded-2xl border border-border bg-card/90 p-4 shadow-sm backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:shadow-md">
                  <p className="text-xs font-medium text-muted-foreground">Average Density</p>
                  <p className="mt-2 text-2xl font-semibold text-foreground">{averageUsersPerTenant}</p>
                  <p className="mt-1 text-xs text-muted-foreground">Users per active tenant</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button className="hover-lift bg-primary text-primary-foreground shadow-sm transition hover:-translate-y-0.5 hover:bg-primary/90" onClick={() => onNavigate('tenants')}>
                  <Building className="mr-2 h-4 w-4" />
                  Manage Tenants
                </Button>
                <Button variant="outline" className="hover-lift border-border bg-card/92 text-foreground shadow-sm transition hover:-translate-y-0.5 hover:border-primary/20 hover:bg-muted hover:text-primary" onClick={() => onNavigate('tenants/create')}>
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Create Tenant
                </Button>
                <Button variant="outline" className="hover-lift border-border bg-card/92 text-foreground shadow-sm transition hover:-translate-y-0.5 hover:border-primary/20 hover:bg-muted hover:text-primary" onClick={() => onNavigate('control')}>
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  Open Control Plane
                </Button>
                <Button variant="outline" className="hover-lift border-border bg-card/92 text-foreground shadow-sm transition hover:-translate-y-0.5 hover:border-primary/20 hover:bg-muted hover:text-primary" onClick={() => onNavigate('audit')}>
                  <Activity className="mr-2 h-4 w-4" />
                  Review Audit
                </Button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-1">
              {platformSignals.map((signal, index) => (
                <button
                  key={signal.label}
                  type="button"
                  onClick={() => onNavigate(signal.tab)}
                  className="animate-slide-up rounded-2xl border border-border bg-card/92 p-4 text-left shadow-sm backdrop-blur-sm transition-all duration-200 hover:-translate-y-1 hover:border-primary/20 hover:shadow-md"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">{signal.label}</p>
                  <p className={`mt-3 text-2xl font-semibold ${signal.tone}`}>{signal.value}</p>
                  <p className="mt-2 text-sm text-muted-foreground">{signal.description}</p>
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <StatSkeletonGrid count={6} className="lg:grid-cols-3" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {kpiCards.map((card) => (
            <StatCard
              key={card.title}
              label={card.title}
              value={card.value}
              hint={card.hint}
              icon={card.icon}
              tone={card.tone}
              onClick={() => setOverviewCardDetail({ title: card.title, value: card.value, description: card.hint, openAction: card.onClick })}
            />
          ))}
        </div>
      )}

      <KpiDetailPanel
        detail={overviewCardDetail}
        onClose={() => setOverviewCardDetail(null)}
        onOpenModule={() => overviewCardDetail?.openAction?.()}
        className="border-primary/20 bg-primary/5"
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {referenceCards.map((card) => {
          const Icon = card.icon;
          return (
            <button
              key={card.title}
              type="button"
              onClick={card.onClick}
              className="card-interactive rounded-2xl border border-border bg-card p-5 text-left shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-primary/20 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{card.title}</p>
                  <p className="mt-3 text-3xl font-semibold text-foreground">{card.value}</p>
                  <p className="mt-2 text-xs text-muted-foreground">{card.hint}</p>
                </div>
                <div className="rounded-full bg-muted p-3">
                  <Icon className={`h-5 w-5 ${card.accent}`} />
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card className="border-border shadow-sm">
          <CardHeader className="border-b border-border/50">
            <CardTitle className="text-hr-lg">Recently Registered Companies</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-6">
            {recentRegisteredCompanies.length === 0 ? (
              <EmptyState icon={Building} title="No recent organizations" description="Newly onboarded organizations will appear here." compact />
            ) : (
              recentRegisteredCompanies.map((company) => (
                <button
                  key={company.id}
                  type="button"
                  onClick={() => onNavigate('tenants')}
                  className="card-interactive flex w-full items-center justify-between rounded-xl border border-border bg-muted/40 p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/20 hover:bg-card"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="h-2.5 w-2.5 rounded-full bg-success" />
                      <p className="truncate text-sm font-semibold text-foreground">{company.name}</p>
                    </div>
                    <p className="mt-1 truncate pl-5 text-xs text-muted-foreground">{company.adminEmail}</p>
                  </div>
                  <p className="whitespace-nowrap pl-4 text-xs text-muted-foreground">{formatRelativeAge(company.createdAt)}</p>
                </button>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm">
          <CardHeader className="border-b border-border/50">
            <CardTitle className="text-hr-lg">Top Performing Plans</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-6">
            {topPerformingPlans.length === 0 ? (
              <EmptyState icon={Layers3} title="No billing performance data" description="Plan revenue rankings will appear once billing activity is recorded." compact />
            ) : (
              topPerformingPlans.map((plan, index) => (
                <button
                  key={plan.planName}
                  type="button"
                  onClick={() => onNavigate('billing/subscriptions')}
                  className="card-interactive flex w-full items-center justify-between rounded-xl border border-border bg-muted/40 p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/20 hover:bg-card"
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                      #{index + 1}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{plan.planName}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{plan.subscribers} subscribers</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-foreground">{formatCurrencyAmount(plan.revenue, 'INR')}</p>
                    <p className="mt-1 text-xs text-muted-foreground">monthly revenue</p>
                  </div>
                </button>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-hr-sm font-medium">Overview Range</p>
          <p className="text-hr-xs text-muted-foreground">Filters charts and activity table for executive snapshots</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-hr-xs text-muted-foreground whitespace-nowrap">
            Last updated at: <span className="font-medium text-foreground">{formatLastUpdated(lastUpdatedAt)}</span>
          </span>
          <Button
            size="icon"
            variant="outline"
            className="h-8 w-8"
            onClick={async () => {
              setIsRefreshing(true);
              await fetchOverviewData();
              setIsRefreshing(false);
            }}
            title="Refresh overview"
            aria-label="Refresh overview"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
          <div className="flex items-center gap-2">
            <Button size="sm" variant={dateRange === 'today' ? 'default' : 'outline'} onClick={() => setDateRange('today')}>Today</Button>
            <Button size="sm" variant={dateRange === '7d' ? 'default' : 'outline'} onClick={() => setDateRange('7d')}>7d</Button>
            <Button size="sm" variant={dateRange === '30d' ? 'default' : 'outline'} onClick={() => setDateRange('30d')}>30d</Button>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <Card className="border-border shadow-sm">
          <CardHeader className="border-b border-border/50">
            <CardTitle className="text-hr-lg">Tenant Watchlist</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-3">
            {tenantWatchlist.map((tenant, index) => (
              <button
                key={tenant.id}
                type="button"
                onClick={() => onNavigate('tenants')}
                className="card-interactive animate-slide-up flex w-full items-center justify-between rounded-xl border border-border bg-card p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/20 hover:bg-muted/40"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div>
                  <p className="text-sm font-semibold text-foreground">{tenant.name}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{tenant.users} users linked</p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={tenant.status === 'active' ? 'success' : tenant.status === 'pending' ? 'warning' : 'danger'}>
                    {tenant.status}
                  </Badge>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </button>
            ))}
            {tenantWatchlist.length === 0 && (
              <EmptyState icon={ShieldCheck} title="No tenant risks detected" description="Suspended, pending or empty tenants will surface here." compact />
            )}
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm">
          <CardHeader className="border-b border-border/50">
            <CardTitle className="text-hr-lg">Recently Added Tenants</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 grid gap-3">
            {recentTenants.map((tenant, index) => (
              <div
                key={tenant.id}
                className="animate-slide-up rounded-xl border border-border bg-muted/40 p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/20 hover:bg-card"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{tenant.name}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{tenant.plan}</p>
                  </div>
                  <Badge variant={String(tenant.status).toLowerCase() === 'active' ? 'success' : String(tenant.status).toLowerCase() === 'pending' ? 'warning' : 'danger'}>
                    {tenant.status}
                  </Badge>
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  Created {tenant.createdAt ? new Date(tenant.createdAt).toLocaleString() : 'recently'}
                </p>
              </div>
            ))}
            {recentTenants.length === 0 && (
              <EmptyState icon={Building} title="No onboarding activity" description="Recently created tenants will be listed here." compact />
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card className="xl:col-span-1">
          <CardHeader><CardTitle className="text-hr-lg">User Status Split</CardTitle></CardHeader>
          <CardContent className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={userStatusData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} dataKey="value">
                  {userStatusData.map((entry, index) => <Cell key={entry.name} fill={chartColor(index)} />)}
                </Pie>
                <Tooltip contentStyle={tooltipContentStyle} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="xl:col-span-1">
          <CardHeader><CardTitle className="text-hr-lg">Tenant Status</CardTitle></CardHeader>
          <CardContent className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={tenantStatusData.length > 0 ? tenantStatusData : [{ name: 'No Data', value: 1, color: '#9CA3AF' }]}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  dataKey="value"
                >
                  {(tenantStatusData.length > 0 ? tenantStatusData : [{ name: 'No Data', color: '#9CA3AF' }]).map((entry, index) => <Cell key={entry.name} fill={tenantStatusData.length > 0 ? chartColor(index) : '#9CA3AF'} />)}
                </Pie>
                <Tooltip contentStyle={tooltipContentStyle} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="xl:col-span-1">
          <CardHeader><CardTitle className="text-hr-lg">Recent Actions</CardTitle></CardHeader>
          <CardContent className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={actionTrendData}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="action" {...axisProps} />
                <YAxis allowDecimals={false} {...axisProps} />
                <Tooltip contentStyle={tooltipContentStyle} cursor={tooltipCursor} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]} fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-hr-lg">Top Tenants By User Count</CardTitle></CardHeader>
        <CardContent className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={usersPerTenant}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="name" hide={false} {...axisProps} />
              <YAxis allowDecimals={false} {...axisProps} />
              <Tooltip contentStyle={tooltipContentStyle} cursor={tooltipCursor} />
              <Bar dataKey="users" radius={[6, 6, 0, 0]} fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-hr-lg">Recent Audit Activity</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{filteredAuditLogs.length} logs ({dateRange})</Badge>
            <Badge variant={failedAuditCount > 0 ? 'danger' : 'success'}>
              <AlertTriangle className="mr-1 h-3 w-3" />
              {failedAuditCount} failed
            </Badge>
            <Button variant="outline" size="sm" onClick={() => onNavigate('audit')}>View All Logs</Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-4">Time</th>
                  <th className="text-left p-4">Action</th>
                  <th className="text-left p-4">Actor</th>
                  <th className="text-left p-4">Target</th>
                  <th className="text-left p-4">Outcome</th>
                </tr>
              </thead>
              <tbody>
                  {recentAuditTable.data.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-0">
                      <EmptyState icon={Clock3} title="No recent activity" description={`No audit events found in the selected ${dateRange} range.`} compact />
                    </td>
                  </tr>
                ) : (
                  recentAuditTable.data.map((log) => (
                    <tr key={log.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="p-4">{formatDateTime(log.created_at)}</td>
                      <td className="p-4"><Badge variant="soft">{log.action || 'unknown'}</Badge></td>
                      <td className="p-4">{log.actor_email || 'System'}</td>
                      <td className="p-4">{log.target || log.request_path || 'N/A'}</td>
                      <td className="p-4"><Badge variant={log.outcome === 'success' ? 'success' : 'danger'}>{log.outcome || 'unknown'}</Badge></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t">
            <TableControls
              totalRows={recentAuditTable.totalRows}
              page={recentAuditTable.page}
              totalPages={recentAuditTable.totalPages}
              pageSize={recentAuditTable.pageSize}
              pageSizes={recentAuditTable.pageSizes}
              onPageSizeChange={recentAuditTable.onPageSizeChange}
              onPrevious={recentAuditTable.goToPreviousPage}
              onNext={recentAuditTable.goToNextPage}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperadminOverviewPage;
